// Diatonic seventh-chord harmonisation for the scale-harmony reference.
//
// Keep this module framework-free: pages choose how to present or voice the
// resulting chords, while this file owns the scale spelling and chord quality.

import { CHORD_TYPES, type ChordTypeId } from './chords';
import { noteNameOf, parseSpelledPitch, transposePitch } from './score/pitch';
import type { Fingering } from './voicing';

export type ScaleHarmonyId =
  | 'major'
  | 'melodic-minor'
  | 'harmonic-minor'
  | 'harmonic-major';

export interface ScaleHarmonyDefinition {
  id: ScaleHarmonyId;
  label: string;
  description: string;
  intervals: readonly [number, number, number, number, number, number, number];
  numerals: readonly [string, string, string, string, string, string, string];
}

export const SCALE_HARMONY_IDS: readonly ScaleHarmonyId[] = [
  'major',
  'melodic-minor',
  'harmonic-minor',
  'harmonic-major',
];

export const SCALE_HARMONIES: Record<ScaleHarmonyId, ScaleHarmonyDefinition> = {
  major: {
    id: 'major',
    label: 'Major',
    description: 'The seven diatonic seventh chords of the major scale.',
    intervals: [0, 2, 4, 5, 7, 9, 11],
    numerals: ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'viiø'],
  },
  'melodic-minor': {
    id: 'melodic-minor',
    label: 'Melodic minor (ascending)',
    description: 'The ascending melodic minor scale, with raised 6th and 7th.',
    intervals: [0, 2, 3, 5, 7, 9, 11],
    numerals: ['i', 'ii', 'III+', 'IV', 'V', 'viø', 'viiø'],
  },
  'harmonic-minor': {
    id: 'harmonic-minor',
    label: 'Harmonic minor',
    description: 'Natural minor with a raised 7th and its leading-tone diminished chord.',
    intervals: [0, 2, 3, 5, 7, 8, 11],
    numerals: ['i', 'iiø', 'III+', 'iv', 'V', 'VI', 'vii°'],
  },
  'harmonic-major': {
    id: 'harmonic-major',
    label: 'Harmonic major',
    description: 'Major with a lowered 6th, combining major and harmonic-minor colours.',
    intervals: [0, 2, 4, 5, 7, 8, 11],
    numerals: ['I', 'iiø', 'iii', 'iv', 'V', 'VI+', 'vii°'],
  },
};

const SEVENTH_CHORD_BY_INTERVALS: Record<string, ChordTypeId> = {
  '0,4,7,11': 'maj7',
  '0,3,7,10': 'min7',
  '0,4,7,10': 'dom7',
  '0,3,6,10': 'min7b5',
  '0,3,6,9': 'dim7',
  '0,4,8,11': 'maj7s5',
  '0,3,7,11': 'minmaj7',
};

/** Spell a scale degree with the correct successive letter name. */
export function spellScaleDegree(
  tonic: string,
  degreeIndex: number,
  semitoneOffset: number,
): string {
  const tonicPitch = parseSpelledPitch(`${tonic}4`);
  return noteNameOf(
    transposePitch(tonicPitch, { degrees: degreeIndex, semitones: semitoneOffset }),
  );
}

export interface HarmonisedScaleChord {
  degree: number;
  numeral: string;
  root: string;
  chordType: ChordTypeId;
  symbol: string;
}

/** Harmonise a seven-note scale in diatonic sevenths. */
export function harmoniseScale(
  tonic: string,
  scaleId: ScaleHarmonyId,
): HarmonisedScaleChord[] {
  const scale = SCALE_HARMONIES[scaleId];
  return scale.intervals.map((interval, index) => {
    const root = spellScaleDegree(tonic, index, interval);
    const stackedIntervals = [0, 2, 4, 6]
      .map((step) => {
        const scaleIndex = index + step;
        const octave = scaleIndex >= scale.intervals.length ? 12 : 0;
        return (
          (scale.intervals[scaleIndex % scale.intervals.length] + octave - interval + 12) % 12
        );
      })
      .sort((a, b) => a - b);
    const chordType = SEVENTH_CHORD_BY_INTERVALS[stackedIntervals.join(',')];
    if (!chordType) {
      throw new Error(`${scale.label} degree ${index + 1} is not a supported seventh chord`);
    }
    return {
      degree: index + 1,
      numeral: scale.numerals[index],
      root,
      chordType,
      symbol: `${root}${CHORD_TYPES[chordType].symbol}`,
    };
  });
}

export const SCALE_HARMONY_MAX_START_FRET = 14;

/**
 * Carry one physical voicing pattern through a practical area of the neck.
 *
 * Each segment moves upwards, but once its lowest played fret would pass the
 * ceiling the whole grip folds down an octave and continues. Octave shifts
 * preserve strings, voicing family and finished inversion.
 */
export function placeVoicingsInNeckWindow<T extends Fingering>(
  voicings: readonly (T | null)[],
  maxStartFret = SCALE_HARMONY_MAX_START_FRET,
): (T | null)[] {
  let previous: T | null = null;

  return voicings.map((voicing) => {
    if (!voicing) return null;

    const octaveSteps = Math.max(
      0,
      ...voicing.frets.map((fret, stringIndex) => {
        const previousFret = previous?.frets[stringIndex];
        if (fret === null || previousFret === null || previousFret === undefined) return 0;
        return Math.floor((previousFret - fret) / 12) + 1;
      }),
    );
    const fretShift = octaveSteps * 12;
    let placed =
      fretShift === 0
        ? voicing
        : ({
            ...voicing,
            frets: voicing.frets.map((fret) => (fret === null ? null : fret + fretShift)),
          } as T);

    const playedFrets = placed.frets.filter((fret): fret is number => fret !== null);
    let lowestFret = playedFrets.length > 0 ? Math.min(...playedFrets) : 0;
    while (lowestFret > maxStartFret && lowestFret >= 12) {
      placed = {
        ...placed,
        frets: placed.frets.map((fret) => (fret === null ? null : fret - 12)),
      } as T;
      lowestFret -= 12;
    }

    previous = placed;
    return placed;
  });
}
